'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { useDrawerPortalContainer } from '@repo/ui/components/ui/drawer-portal-context';
import { cn } from '@repo/ui/lib/utils';

// ------------------------------------------------------------------
//  Types
// ------------------------------------------------------------------

type PopoverContextValue = {
  anchorRef: React.RefObject<Element | null>;
};

// ------------------------------------------------------------------
//  Popover context (used for custom PopoverAnchor → Positioner.anchor)
// ------------------------------------------------------------------

const PopoverContext = React.createContext<null | PopoverContextValue>(null);

// ------------------------------------------------------------------
//  Popover
// ------------------------------------------------------------------

function Popover(t0) {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const anchorRef = React.useRef(null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      anchorRef
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props) {
    t2 = <PopoverContext.Provider value={t1} data-sentry-element="PopoverContext.Provider" data-sentry-component="Popover" data-sentry-source-file="popover.tsx"><PopoverPrimitive.Root data-slot="popover" {...props} data-sentry-element="PopoverPrimitive.Root" data-sentry-source-file="popover.tsx" /></PopoverContext.Provider>;
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// ------------------------------------------------------------------
//  PopoverAnchor
// ------------------------------------------------------------------

function PopoverAnchor({
  asChild,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  asChild?: boolean;
}) {
  const {
    anchorRef
  } = usePopoverContext();
  const asChildElement = asChild && React.isValidElement<Record<string, unknown>>(children) ? children : null;
  const childRef = asChildElement ? (asChildElement as React.ReactElement & {
    ref?: React.Ref<Element>;
  }).ref : undefined;
  const mergedRef = useMergedRefs(anchorRef, childRef);
  if (asChildElement) {
    return React.cloneElement(asChildElement, {
      ref: mergedRef,
      ...props
    });
  }
  return <div ref={mergedRef} {...props} data-sentry-component="PopoverAnchor" data-sentry-source-file="popover.tsx">
      {children}
    </div>;
}

// ------------------------------------------------------------------
//  PopoverContent
// ------------------------------------------------------------------

function PopoverContent(t0) {
  const $ = _c(41);
  let arrowPadding;
  let className;
  let collisionAvoidance;
  let collisionBoundary;
  let collisionPadding;
  let container;
  let disableAnchorTracking;
  let positionMethod;
  let props;
  let sticky;
  let style;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      align: t1,
      alignOffset: t2,
      arrowPadding,
      className,
      collisionAvoidance,
      collisionBoundary,
      collisionPadding,
      container,
      disableAnchorTracking,
      positionMethod,
      side: t3,
      sideOffset: t4,
      sticky,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = arrowPadding;
    $[2] = className;
    $[3] = collisionAvoidance;
    $[4] = collisionBoundary;
    $[5] = collisionPadding;
    $[6] = container;
    $[7] = disableAnchorTracking;
    $[8] = positionMethod;
    $[9] = props;
    $[10] = sticky;
    $[11] = style;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    arrowPadding = $[1];
    className = $[2];
    collisionAvoidance = $[3];
    collisionBoundary = $[4];
    collisionPadding = $[5];
    container = $[6];
    disableAnchorTracking = $[7];
    positionMethod = $[8];
    props = $[9];
    sticky = $[10];
    style = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
  }
  const align = t1 === undefined ? "center" : t1;
  const alignOffset = t2 === undefined ? 0 : t2;
  const side = t3 === undefined ? "bottom" : t3;
  const sideOffset = t4 === undefined ? 4 : t4;
  const {
    anchorRef
  } = usePopoverContext();
  const drawerContainer = useDrawerPortalContainer();
  const t5 = container ?? drawerContainer;
  let t6;
  if ($[16] !== className) {
    t6 = cn("z-100 w-72 max-w-(--available-width) origin-[var(--transform-origin)] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden duration-100 data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[ending-style]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[starting-style]:animate-in data-[starting-style]:fade-in-0 data-[starting-style]:zoom-in-95", className);
    $[16] = className;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== style) {
    t7 = {
      pointerEvents: "auto",
      ...style
    };
    $[18] = style;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== props || $[21] !== t6 || $[22] !== t7) {
    t8 = <PopoverPrimitive.Popup className={t6} data-slot="popover-content" style={t7} {...props} data-sentry-element="PopoverPrimitive.Popup" data-sentry-source-file="popover.tsx" />;
    $[20] = props;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== align || $[25] !== alignOffset || $[26] !== anchorRef || $[27] !== arrowPadding || $[28] !== collisionAvoidance || $[29] !== collisionBoundary || $[30] !== collisionPadding || $[31] !== disableAnchorTracking || $[32] !== positionMethod || $[33] !== side || $[34] !== sideOffset || $[35] !== sticky || $[36] !== t8) {
    t9 = <PopoverPrimitive.Positioner align={align} alignOffset={alignOffset} anchor={anchorRef} arrowPadding={arrowPadding} className="isolate z-70" collisionAvoidance={collisionAvoidance} collisionBoundary={collisionBoundary} collisionPadding={collisionPadding} disableAnchorTracking={disableAnchorTracking} positionMethod={positionMethod} side={side} sideOffset={sideOffset} sticky={sticky} data-sentry-element="PopoverPrimitive.Positioner" data-sentry-source-file="popover.tsx">{t8}</PopoverPrimitive.Positioner>;
    $[24] = align;
    $[25] = alignOffset;
    $[26] = anchorRef;
    $[27] = arrowPadding;
    $[28] = collisionAvoidance;
    $[29] = collisionBoundary;
    $[30] = collisionPadding;
    $[31] = disableAnchorTracking;
    $[32] = positionMethod;
    $[33] = side;
    $[34] = sideOffset;
    $[35] = sticky;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t5 || $[39] !== t9) {
    t10 = <PopoverPrimitive.Portal container={t5} data-sentry-element="PopoverPrimitive.Portal" data-sentry-component="PopoverContent" data-sentry-source-file="popover.tsx">{t9}</PopoverPrimitive.Portal>;
    $[38] = t5;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
}

// ------------------------------------------------------------------
//  PopoverDescription
// ------------------------------------------------------------------

function PopoverDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <PopoverPrimitive.Description className={t1} data-slot="popover-description" {...props} data-sentry-element="PopoverPrimitive.Description" data-sentry-component="PopoverDescription" data-sentry-source-file="popover.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// ------------------------------------------------------------------
//  PopoverHeader
// ------------------------------------------------------------------

function PopoverHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-0.5 text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} data-slot="popover-header" {...props} data-sentry-component="PopoverHeader" data-sentry-source-file="popover.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// ------------------------------------------------------------------
//  PopoverTitle
// ------------------------------------------------------------------

function PopoverTitle(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("font-medium", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <PopoverPrimitive.Title className={t1} data-slot="popover-title" {...props} data-sentry-element="PopoverPrimitive.Title" data-sentry-component="PopoverTitle" data-sentry-source-file="popover.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// ------------------------------------------------------------------
//  PopoverTrigger
// ------------------------------------------------------------------

function PopoverTrigger(t0) {
  const $ = _c(10);
  let asChild;
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      asChild,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = children;
    $[3] = props;
  } else {
    asChild = $[1];
    children = $[2];
    props = $[3];
  }
  if (asChild && React.isValidElement(children)) {
    let t1;
    if ($[4] !== children || $[5] !== props) {
      t1 = <PopoverPrimitive.Trigger {...props} render={children} />;
      $[4] = children;
      $[5] = props;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  let t1;
  if ($[7] !== children || $[8] !== props) {
    t1 = <PopoverPrimitive.Trigger {...props} data-sentry-element="PopoverPrimitive.Trigger" data-sentry-component="PopoverTrigger" data-sentry-source-file="popover.tsx">{children}</PopoverPrimitive.Trigger>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}

// ------------------------------------------------------------------
//  returnFocusUnlessMoved
// ------------------------------------------------------------------

/**
 * Builds a `finalFocus` handler for PopoverContent that returns focus to `target` only while
 * focus is still inside the popover, on the target, or nowhere. Base UI skips that guard for
 * explicit targets, which would pull focus away from a field the user already moved to while the
 * popover is still closing. Focus on an element around the target counts as nowhere: a modal
 * dialog's popup takes the focus that falls out of a closing popover.
 */
function returnFocusUnlessMoved(target: React.RefObject<HTMLElement | null>, popup: React.RefObject<HTMLElement | null>) {
  return () => {
    const active = document.activeElement;
    const focusIsElsewhere = active !== null && !active.contains(target.current) && popup.current?.contains(active) !== true;
    return focusIsElsewhere ? false : target.current;
  };
}

// ------------------------------------------------------------------
//  usePopoverContext
// ------------------------------------------------------------------

function usePopoverContext() {
  const ctx = React.useContext(PopoverContext);
  if (!ctx) {
    throw new Error("Popover parts must be used within <Popover>");
  }
  return ctx;
}
export { Popover, PopoverAnchor, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger, returnFocusUnlessMoved };